import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import Http from "../services/Http";
import { toastHandler } from "../../utils/utils";
import { messages } from "../../utils/messages";

// Initial state for the zone slice
const initialState = {
  // zoneData: {},
  zoneData: {
    data: [], // Initialize data as an empty array
  },
  fixtureData: [],
  zoneFixtureData: [],
  zoneFixtureIncidentData: [],
  status: "idle",
  locationId: "",
};

// Destructure the httpRequest function from Http service
const { httpRequest } = Http();

/**
 * Async thunk for fetching the list of zone.
 * @returns {Array} - List of zone.
 */
export const zoneList = createAsyncThunk(
  "listZone",
  async ({ locationId, pageCount, navigate, publish }, { rejectWithValue }) => {
    try {
      const data = await httpRequest(
        {
          method: "GET",
          url: `admin/zones?location_id=${locationId}&page=${pageCount}`,
        },
        navigate
      );
      return {
        data,
        locationId,
        publish,
      };
    } catch (err) {
      return rejectWithValue(err.message || messages.listZone.error, {
        cause: err.cause,
      });
    }
  }
);

/**
 * Async thunk for printing a zone.
 * This function dispatches a POST request to print the specified zone.
 *
 * @param {Object} param - The parameter object.
 * @param {string} param.id - The ID of the zone to be printed.
 * @param {Object} thunkAPI - The thunk API object, providing dispatch, getState, and other utilities.
 * @returns {Object} - The response from the HTTP request, or an error object in case of failure.
 *
 * @throws Will throw an error if the HTTP request fails.
 */
export const printZone = createAsyncThunk(
  "printingZone",
  async ({ id, testKey, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "POST",
          body: {
            zone_id: id,
            test: testKey,
          },
          url: "admin/zones/print",
        },
        navigate
      );
    } catch (err) {
      return rejectWithValue(err.message || messages.printingZone.error, {
        cause: err.cause,
      });
    }
  }
);

/**
 * Async thunk for publish a zone.
 * This function dispatches a POST request to print the specified zone.
 *
 * @param {Object} param - The parameter object.
 * @param {string} param.id - The ID of the zone to be publish.
 * @param {Object} thunkAPI - The thunk API object, providing dispatch, getState, and other utilities.
 * @returns {Object} - The response from the HTTP request, or an error object in case of failure.
 *
 * @throws Will throw an error if the HTTP request fails.
 */
export const publishZone = createAsyncThunk(
  "publishedZone",
  async ({ id, navigate }, { rejectWithValue }) => {
    try {
      const blob = await httpRequest(
        {
          method: "POST",
          body: { zone_id: id },
          url: "admin/zones/publish",
          responseType: "blob", // Set response type to blob
        },
        navigate
      );

      // Do something with the Blob, such as generating a URL
      const url = URL.createObjectURL(blob);

      return { id, url }; // Return the URL instead of the Blob
    } catch (err) {
      return rejectWithValue(err.message || "Error publishing zone", {
        cause: err.cause,
      });
    }
  }
);

/**
 * Async thunk for Download a zone.
 * This function dispatches a POST request to print the specified zone.
 *
 * @param {Object} param - The parameter object.
 * @param {string} param.id - The ID of the zone to be publish.
 * @param {Object} thunkAPI - The thunk API object, providing dispatch, getState, and other utilities.
 * @returns {Object} - The response from the HTTP request, or an error object in case of failure.
 *
 * @throws Will throw an error if the HTTP request fails.
 */
export const downloadZone = createAsyncThunk(
  "downloadedZone",
  async ({ id, type, navigate }, { rejectWithValue }) => {
    try {
      const blob = await httpRequest(
        {
          method: "POST",
          body: {
            zone_id: id,
            test: type,
          },
          url: "admin/zones/scorm",
          responseType: "blob", // Set response type to blob
        },
        navigate
      );

      // Do something with the Blob, such as generating a URL
      const url = URL.createObjectURL(blob);

      return { id, url }; // Return the URL instead of the Blob
    } catch (err) {
      return rejectWithValue(err.message || "Error publishing zone", {
        cause: err.cause,
      });
    }
  }
);

/**
 * Async thunk for adding a new zone.
 * @param {Object} data - The zone data to be added.
 * @returns {Object} - The added zone data.
 */
export const addZone = createAsyncThunk(
  "createZone",
  async ({ data, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "POST",
          body: data,
          url: "admin/zones",
        },
        navigate
      );
    } catch (err) {
      return rejectWithValue(err.message || messages.createZone.error, {
        cause: err.cause,
      });
    }
  }
);

/**
 * Async thunk for updating an existing zone.
 * @param {Object} data - The zone data to be updated.
 * @param {string} id - The ID of the zone to be updated.
 * @returns {Object} - The updated zone data.
 */
export const updateZone = createAsyncThunk(
  "editZone",
  async ({ data, id, navigate }, { rejectWithValue }) => {
    try {
      await httpRequest(
        {
          method: "PUT",
          body: data,
          url: `admin/zones/${id}`,
        },
        navigate
      );
      return { data, id };
    } catch (err) {
      return rejectWithValue(err.message || messages.editZone.error, {
        cause: err.cause,
      });
    }
  }
);

/**
 * Async thunk for deleting a zone by ID.
 * @param {string} id - The ID of the zone to be deleted.
 * @returns {Object} - The response from the delete request.
 */
export const deleteZone = createAsyncThunk(
  "deletedZone",
  async ({ id, locationId, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "DELETE",
          url: `admin/zones/${id}?location_id=${locationId}`,
        },
        navigate
      );
    } catch (err) {
      return rejectWithValue(err.message || messages.deletedZone.error, {
        cause: err.cause,
      });
    }
  }
);

/**
 * Async thunk for getting Fixture by zone ID.
 * @param {string} id - The ID of the zone.
 * @returns {Array} - List of Fixture.
 */
export const getZoneFixture = createAsyncThunk(
  "zoneFixture",
  async ({ id, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "GET",
          url: `admin/zone-fixtures/${id}`,
        },
        navigate
      );
    } catch (err) {
      return rejectWithValue(err.message || messages.getZoneFixture.error, {
        cause: err.cause,
      });
    }
  }
);

/**
 * Async thunk for getting Fixture by zone ID.
 * @param {string} id - The ID of the zone.
 * @returns {Array} - List of Fixture.
 */
export const getZoneFixtureIncident = createAsyncThunk(
  "zoneFixtureIncident",
  async ({ id, navigate }, { rejectWithValue }) => {
    try {
      return await httpRequest(
        {
          method: "GET",
          url: `admin/zone-fixtures/incidents/${id}`,
        },
        navigate
      );
    } catch (err) {
      return rejectWithValue(
        err.message || messages.getZoneFixtureIncident.error,
        {
          cause: err.cause,
        }
      );
    }
  }
);

/**
 * Async thunk for deleting a zone fixture by ID.
 * @param {string} id - The ID of the zone fixture to be deleted.
 * @returns {Object} - The response from the delete request.
 */
export const deleteZoneFixture = createAsyncThunk(
  "deletedZoneFixture",
  async ({ id, selectedZoneId, navigate }, { rejectWithValue }) => {
    try {
      await httpRequest(
        {
          method: "DELETE",
          url: `admin/zone-fixtures/${id}?zone_id=${selectedZoneId}`,
        },
        navigate
      );
      return { id };
    } catch (err) {
      return rejectWithValue(err.message || messages.deletedZoneFixture.error, {
        cause: err.cause,
      });
    }
  }
);

/**
 * Async thunk for fetching the list of campuses.
 * @returns {Array} - List of campuses.
 */
export const getAllFixture = createAsyncThunk(
  "fixtures",
  async (_, { rejectWithValue }) => {
    try {
      return await httpRequest({
        method: "GET",
        url: "admin/fixtures",
      });
    } catch (err) {
      return rejectWithValue(err.message || messages.listFixtures.error, {
        cause: err.cause,
      });
    }
  }
);

// Create a slice for zone-related actions and state management
const zoneSlice = createSlice({
  name: "zonePoint",
  initialState,
  reducers: {
    /**
     * Reducer for showing toast messages.
     * @param {Object} state - The current state.
     * @param {Object} action - The action containing payload data.
     */
    showToast: (state, action) => {
      const { success, message, navigate, navigatePath, locationState } =
        action.payload;
      toastHandler(success, message, navigate, navigatePath, locationState);
    },
    setClearZoneData: (state, action) => {
      // console.log("stateeeeee", state);
      // console.log("actionnnnn", action);
      state.zoneData = [];
    },
    // updateZoneFixturesCount: (state, action) => {
    //   state.zoneData.data = state.zoneData.data.map((item) => {
    //     if (item.id === parseInt(action.payload)) {
    //       return {
    //         ...item,
    //         zone_fixtures_count: item.zone_fixtures_count + 1,
    //       };
    //     }
    //     return item;
    //   });
    // },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        (action) =>
          [
            zoneList.pending,
            printZone.pending,
            publishZone.pending,
            downloadZone.pending,
            addZone.pending,
            deleteZone.pending,
            updateZone.pending,
            getZoneFixture.pending,
            getZoneFixtureIncident.pending,
            getAllFixture.pending,
            deleteZoneFixture.pending,
          ].some((type) => type.match(action)),
        (state, action) => {
          state.status = "loading";
          action.meta.arg.setLoader(true);
        }
      )
      .addMatcher(
        (action) =>
          [
            zoneList.fulfilled,
            printZone.fulfilled,
            publishZone.fulfilled,
            downloadZone.fulfilled,
            addZone.fulfilled,
            deleteZone.fulfilled,
            updateZone.fulfilled,
            getZoneFixture.fulfilled,
            getZoneFixtureIncident.fulfilled,
            getAllFixture.fulfilled,
            deleteZoneFixture.fulfilled,
          ].some((type) => type.match(action)),
        (state, action) => {
          // console.log("stateeee", state.locationId);
          // console.log("action", action);

          state.status = "succeeded";

          // if (["listZone"].includes(action.type.split("/")[0])) {
          //   state.zoneData = action.payload.zones;
          // }

          // Update state in case of edit
          // if (action.type === "editZone/fulfilled") {
          //   const selectedzone = state.zoneData.data.find(
          //     (zone) => zone.id === action.payload.id
          //   );
          //   if (selectedzone) {
          //     selectedzone.name = action.payload.data.name;
          //     selectedzone.notes = action.payload.data.notes;
          //   }
          // }

          if (action.type === "listZone/fulfilled") {
            // August
            if (action.payload.publish === "publish") {
              state.zoneData = {
                data: [],
              };
            }

            if (state.locationId !== action.payload.locationId) {
              state.zoneData = {
                data: [],
              };
            }

            state.locationId = action.payload.locationId;

            // const existingData = state.zoneData.data;
            // const newData = action.payload.data.zones.data;

            const existingData = Array.isArray(state.zoneData.data)
              ? state.zoneData.data
              : [];

            const newData = Array.isArray(action.payload.data?.zones?.data)
              ? action.payload.data.zones.data
              : [];

            const mergedData = [...existingData, ...newData].reduce(
              (acc, current) => {
                if (!acc.find((item) => item.id === current.id)) {
                  acc.push(current);
                }
                return acc;
              },
              []
            );

            state.zoneData = {
              ...action.payload.data.zones,
              data: mergedData,
            };
          }

          // Create
          if ("createZone" === action.type.split("/")[0]) {
            state.zoneData = {
              data: [],
            };
          }

          // Update state in case of edit
          if ("editZone" === action.type.split("/")[0]) {
            const selectedzone = state.zoneData.data.find(
              (zone) => zone.id === action.payload.id
            );
            if (selectedzone) {
              selectedzone.name = action.payload.data.name;
              selectedzone.notes = action.payload.data.notes;
            }
          }

          // Delete
          if ("deletedZone" === action.type.split("/")[0]) {
            state.zoneData = {
              data: [],
            };
          }

          // August
          // if ("printingZone" === action.type.split("/")[0]) {
          //   state.zoneData = {
          //     data: [],
          //   };
          // }

          // get fixtures data
          if (action.type === "fixtures/fulfilled") {
            state.fixtureData = action.payload.fixtures;
          }

          //get zone fixtures data
          if (action.type === "zoneFixture/fulfilled") {
            state.zoneFixtureData = action.payload.zoneFixtures;
          }

          //get zone fixtures incident data
          if (action.type === "zoneFixtureIncident/fulfilled") {
            state.zoneFixtureIncidentData = action.payload.incidentTypes;
          }

          // Update zone fixtures delete
          if (action.type === "deletedZoneFixture/fulfilled") {
            state.zoneFixtureData = state.zoneFixtureData.filter(
              (fixture) => fixture.id !== action.payload.id
            );
          }

          action.meta.arg.setLoader(false);
          // Show toast only in case of add, edit, delete Assembly
          if (
            [
              // "printingZone/fulfilled",
              // "publishedZone/fulfilled",
              "createZone/fulfilled",
              "editZone/fulfilled",
              "deletedZone/fulfilled",
              "deletedZoneFixture/fulfilled",
            ].includes(action.type)
          ) {
            zoneSlice.caseReducers.showToast(state, {
              payload: {
                success: true,
                message:
                  action?.payload?.message ||
                  messages[action.type.split("/")[0]].success,
              },
            });
          }
        }
      )
      .addMatcher(
        (action) =>
          [
            zoneList.rejected,
            printZone.rejected,
            publishZone.rejected,
            downloadZone.rejected,
            addZone.rejected,
            deleteZone.rejected,
            updateZone.rejected,
            getZoneFixture.rejected,
            getZoneFixtureIncident.rejected,
            getAllFixture.rejected,
            deleteZoneFixture.rejected,
          ].some((type) => type.match(action)),
        (state, action) => {
          state.status = "failed";
          action.meta.arg.setLoader(false);
          zoneSlice.caseReducers.showToast(state, {
            payload: {
              success: false,
              message: action.payload,
            },
          });
        }
      );
  },
});

// export const { updateZoneFixturesCount } = zoneSlice.actions;

// Action creators are generated for each case reducer function
export const { setClearZoneData } = zoneSlice.actions;

export default zoneSlice.reducer;
